// frontend/src/components/engineer/pages/NoBreakPage.tsx - COMPLETE WITH DEFERRED VALIDATION
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  ClockIcon,
  UserGroupIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  DocumentArrowUpIcon,
  CalendarDaysIcon,
  UserIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  InformationCircleIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import { 
  noBreakService, 
  NoBreakEmployee, 
  NoBreakRequestData, 
  NoBreakResult 
} from '../../../services/noBreak.service';
import { excelProcessingService } from '../../../services/excelProcessing.service';
import { AuthUser } from '../../../services/auth.service';
import NoBreakReportPage from './NoBreakReportPage';
import styles from './CreateOvertimePage.module.css';

interface NoBreakPageProps {
  user?: AuthUser;
  onNavigate?: (page: string, params?: any) => void;
}

interface FormData {
  projectName: string;
  projectCode: string;
  selectedDate: Date;
  selectedEmployeeIds: string[];
  reason: string;
  searchPin: string;
}

interface FormErrors {
  date?: string;
  employees?: string;
  reason?: string;
  pin?: string;
  general?: string;
  projectName?: string;
  projectCode?: string;
}

interface LoadingProgress {
  stage: string;
  current: number;
  total: number;
  message: string;
}

const NoBreakPage: React.FC<NoBreakPageProps> = ({ user, onNavigate }) => {
  // Form State
  const [formData, setFormData] = useState<FormData>({
    projectName: '',
    projectCode: '',
    selectedDate: new Date(),
    selectedEmployeeIds: [],
    reason: '',
    searchPin: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});

  // Data State
  const [eligibleEmployees, setEligibleEmployees] = useState<NoBreakEmployee[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<NoBreakEmployee[]>([]);

  // UI State
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [submissionResult, setSubmissionResult] = useState<{
    success: boolean;
    message: string;
    requestId?: string;
  } | null>(null);

  // Progress State
  const [loadingProgress, setLoadingProgress] = useState<LoadingProgress>({
    stage: 'initializing',
    current: 0,
    total: 100,
    message: 'Initializing system...'
  });

  // Excel Upload State
  const [isUploadingExcel, setIsUploadingExcel] = useState(false);
  const [excelUploadResult, setExcelUploadResult] = useState<any>(null);
  const [showExcelUpload, setShowExcelUpload] = useState(false);

  // PIN Search State
  const [pinSearchResult, setPinSearchResult] = useState<NoBreakEmployee | null>(null);
  const [isSearchingPin, setIsSearchingPin] = useState(false);

  // Report State
  const [showReport, setShowReport] = useState(false);
  const [reportData, setReportData] = useState<any>(null);

  // Initialize component
  useEffect(() => {
    initializeData();
    setTodayDate();
  }, []);

  // Filter employees based on search
  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = eligibleEmployees.filter(emp => 
        emp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        emp.designation.toLowerCase().includes(searchQuery.toLowerCase()) ||
        emp.department.toLowerCase().includes(searchQuery.toLowerCase()) ||
        emp.employeeNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        emp.pin?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredEmployees(filtered);
    } else {
      setFilteredEmployees(eligibleEmployees);
    }
  }, [searchQuery, eligibleEmployees]);

  const initializeData = async () => {
    try {
      setIsLoading(true);
      
      // Stage 1: Permission Check (0-20%)
      setLoadingProgress({
        stage: 'permissions',
        current: 10,
        total: 100,
        message: 'Checking permissions...'
      });
      
      if (!user?.hasOvertimeAccess) {
        setErrors({ 
          general: 'You do not have permission to manage no-break records. Please contact your administrator.' 
        });
        setIsLoading(false);
        return;
      }

      // Stage 2: Building UUID Mapping (20-40%)
      setLoadingProgress({
        stage: 'mapping',
        current: 30,
        total: 100,
        message: 'Building employee UUID mapping...'
      });

      // Stage 3: Loading Employee Data (40-90%)
      setLoadingProgress({
        stage: 'employees',
        current: 50,
        total: 100,
        message: 'Loading employee data from MasterSheet...'
      });

      // 🆕 UPDATED: No attendance checking - just load employees
      const employees = await noBreakService.getEligibleEmployeesWithProgress(
        (current: number, total: number, stage: string) => {
          setLoadingProgress({
            stage: 'loading',
            current,
            total,
            message: stage
          });
        }
      );
      
      setLoadingProgress({
        stage: 'finalizing',
        current: 100,
        total: 100,
        message: 'Finalizing data...'
      });

      setEligibleEmployees(employees);
      setFilteredEmployees(employees);

      console.log(`✅ Loaded ${employees.length} employees (attendance validation deferred)`);

    } catch (error) {
      console.error('❌ Error initializing data:', error);
      setErrors({ general: 'Failed to load data. Please refresh and try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const setTodayDate = () => {
    const today = new Date();
    setFormData(prev => ({ ...prev, selectedDate: today }));
  };

  // Force refresh data
  const handleRefresh = useCallback(async () => {
    console.log('🔄 Force refreshing data...');
    try {
      setIsLoading(true);
      await noBreakService.forceRefresh();
      await initializeData();
    } catch (error) {
      console.error('❌ Error refreshing data:', error);
      setErrors({ general: 'Failed to refresh data. Please try again.' });
    }
  }, []);

  // Validation
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Project validation
    if (!formData.projectName.trim()) {
      newErrors.projectName = 'Project name is required';
    }
    
    if (!formData.projectCode.trim()) {
      newErrors.projectCode = 'Project code is required';
    }

    // Date validation
    const dateValidation = noBreakService.validateDateSelection(formData.selectedDate);
    if (!dateValidation.valid) {
      newErrors.date = dateValidation.error;
    }

    // Employees validation
    if (formData.selectedEmployeeIds.length === 0) {
      newErrors.employees = 'At least one employee must be selected';
    }

    // Reason validation
    if (!formData.reason.trim()) {
      newErrors.reason = 'Reason for no-break is required';
    } else if (formData.reason.trim().length < 5) {
      newErrors.reason = 'Reason must be at least 5 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Show Report Page
  const handleShowReport = () => {
    if (!validateForm()) {
      return;
    }

    const selectedEmployees = eligibleEmployees.filter(emp => 
      formData.selectedEmployeeIds.includes(emp.id)
    );

    const reportData = {
      projectName: formData.projectName,
      projectCode: formData.projectCode,
      selectedDate: formData.selectedDate,
      reason: formData.reason,
      employees: selectedEmployees,
      appliedBy: user?.name || 'Unknown User',
      appliedAt: new Date()
    };

    setReportData(reportData);
    setShowReport(true);
  };

  // Back from Report
  const handleBackFromReport = () => {
    setShowReport(false);
    setReportData(null);
  };

  // Submit no-break request
  const handleSubmit = async () => {
    if (!validateForm() || !user) {
      return;
    }

    const userId = user.employeeNo || user.employeeNumber || user.uid;
    const userName = user.name || user.displayName || '';
    
    if (!userId) {
      setSubmissionResult({
        success: false,
        message: 'User identification error. Please logout and login again.'
      });
      return;
    }

    setIsSubmitting(true);
    setSubmissionResult(null);

    try {
      const requestData: NoBreakRequestData = {
        projectName: formData.projectName.trim(),
        projectCode: formData.projectCode.trim(),
        selectedDate: formData.selectedDate,
        employeeIds: formData.selectedEmployeeIds,
        requesterId: userId,
        requesterName: userName,
        reason: formData.reason.trim()
      };

      const result = await noBreakService.applyNoBreakStatus(requestData);
      setSubmissionResult(result);

      if (result.success) {
        // Reset form
        setFormData({
          projectName: '',
          projectCode: '',
          selectedDate: new Date(),
          selectedEmployeeIds: [],
          reason: '',
          searchPin: ''
        });
        
        setSearchQuery('');
        setExcelUploadResult(null);
        setShowExcelUpload(false);
        setPinSearchResult(null);
      }

    } catch (error) {
      console.error('Error submitting no-break request:', error);
      setSubmissionResult({
        success: false,
        message: `Submission failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Excel upload handler
  const handleExcelUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validation = excelProcessingService.validateExcelFile(file);
    if (!validation.valid) {
      setErrors({ employees: validation.error });
      event.target.value = '';
      return;
    }

    setIsUploadingExcel(true);
    setExcelUploadResult(null);
    setErrors({});

    try {
      const processingResult = await excelProcessingService.processExcelFile(file);
      
      if (!processingResult.success) {
        throw new Error(processingResult.errors.join(', '));
      }

      const matchingResult = excelProcessingService.matchEmployees(
        processingResult.auxNumbers,
        eligibleEmployees
      );

      setExcelUploadResult(matchingResult);

      if (matchingResult.matched.length > 0) {
        const matchedIds = matchingResult.matched.map(emp => emp.id);
        setFormData(prev => ({
          ...prev,
          selectedEmployeeIds: [...new Set([...prev.selectedEmployeeIds, ...matchedIds])]
        }));
      }

    } catch (error) {
      console.error('❌ Error processing Excel file:', error);
      setExcelUploadResult({
        success: false,
        matched: [],
        failed: [],
        total: 0
      });
      setErrors({
        employees: error instanceof Error ? error.message : 'Failed to process Excel file'
      });
    } finally {
      setIsUploadingExcel(false);
      event.target.value = '';
    }
  }, [eligibleEmployees]);

  // PIN search handler
  const handlePinSearch = useCallback(async () => {
    if (!formData.searchPin.trim()) {
      setErrors({ pin: 'Please enter a PIN/AUX number' });
      return;
    }

    setIsSearchingPin(true);
    setPinSearchResult(null);
    setErrors({ ...errors, pin: undefined });

    try {
      const employee = await noBreakService.searchEmployeeByPin(formData.searchPin.trim());
      
      if (employee) {
        setPinSearchResult(employee);
        console.log(`✅ Found employee: ${employee.name}`);
      } else {
        setErrors({ pin: 'Employee not found with this PIN/AUX number' });
      }
    } catch (error) {
      console.error('❌ Error searching employee:', error);
      setErrors({ pin: 'Error searching employee. Please try again.' });
    } finally {
      setIsSearchingPin(false);
    }
  }, [formData.searchPin, errors]);

  // Add PIN search result to selection
  const handleAddPinResult = useCallback(() => {
    if (pinSearchResult && !formData.selectedEmployeeIds.includes(pinSearchResult.id)) {
      setFormData(prev => ({
        ...prev,
        selectedEmployeeIds: [...prev.selectedEmployeeIds, pinSearchResult.id]
      }));
      setPinSearchResult(null);
      setFormData(prev => ({ ...prev, searchPin: '' }));
      setErrors({ ...errors, pin: undefined });
    }
  }, [pinSearchResult, formData.selectedEmployeeIds, errors]);

  // Employee selection handlers
  const handleEmployeeToggle = (employeeId: string) => {
    setFormData(prev => ({
      ...prev,
      selectedEmployeeIds: prev.selectedEmployeeIds.includes(employeeId)
        ? prev.selectedEmployeeIds.filter(id => id !== employeeId)
        : [...prev.selectedEmployeeIds, employeeId]
    }));
  };

  const handleSelectAllEmployees = () => {
    // 🆕 UPDATED: Select ALL employees - no attendance filter
    setFormData(prev => ({
      ...prev,
      selectedEmployeeIds: filteredEmployees.map(emp => emp.id)
    }));
  };

  const handleClearAllEmployees = () => {
    setFormData(prev => ({ ...prev, selectedEmployeeIds: [] }));
    setExcelUploadResult(null);
    setPinSearchResult(null);
  };

  // Computed values
  const selectedEmployees = useMemo(() => 
    eligibleEmployees.filter(emp => formData.selectedEmployeeIds.includes(emp.id)),
    [eligibleEmployees, formData.selectedEmployeeIds]
  );

  // Show Report Page
  if (showReport && reportData) {
    return (
      <NoBreakReportPage 
        reportData={reportData}
        onBack={handleBackFromReport}
      />
    );
  }

  // Access check
  if (!user?.hasOvertimeAccess && !isLoading) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorContent}>
          <ExclamationTriangleIcon className={styles.errorIcon} />
          <h3 className={styles.errorTitle}>Access Denied</h3>
          <p className={styles.errorMessage}>
            You do not have permission to manage no-break records. Please contact your administrator.
          </p>
          <button 
            onClick={() => onNavigate?.('dashboard')} 
            className={styles.retryButton}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Loading state with enhanced card
  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f7f5f2 0%, #faf9f7 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem 1rem'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '1.5rem',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          border: '1px solid #e8e3dc',
          padding: '3rem 2.5rem',
          maxWidth: '500px',
          width: '100%',
          textAlign: 'center'
        }}>
          {/* Phoenician Logo */}
          <img 
            src="/images/Phoenicianogo.png" 
            alt="Phoenician Logo" 
            style={{
              width: '180px',
              height: 'auto',
              marginBottom: '2rem',
              opacity: 0.9
            }}
          />
          
          {/* Loading Title */}
          <h2 style={{
            margin: '0 0 1rem 0',
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#322821',
            letterSpacing: '-0.025em'
          }}>
            Loading No-Break System...
          </h2>
          
          {/* Stage Information */}
          <div style={{
            background: 'linear-gradient(135deg, #f0f4f8, #e2e8f0)',
            padding: '1rem 1.5rem',
            borderRadius: '1rem',
            marginBottom: '2rem',
            border: '1px solid #cbd5e0'
          }}>
            <div style={{
              fontSize: '0.875rem',
              color: '#4a5568',
              fontWeight: '600',
              marginBottom: '0.5rem',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              Stage: {loadingProgress.stage}
            </div>
            <div style={{
              fontSize: '1rem',
              color: '#2d3748',
              fontWeight: '500',
              lineHeight: '1.5'
            }}>
              {loadingProgress.message}
            </div>
          </div>
          
          {/* Progress Bar */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{
              background: 'linear-gradient(135deg, #e2e8f0, #edf2f7)',
              borderRadius: '0.75rem',
              height: '12px',
              overflow: 'hidden',
              position: 'relative',
              border: '1px solid #cbd5e0'
            }}>
              <div style={{
                background: 'linear-gradient(90deg, #c29e5d 0%, #d4b373 50%, #c29e5d 100%)',
                height: '100%',
                width: `${loadingProgress.current}%`,
                transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                borderRadius: '0.75rem',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                  animation: 'shine 2s infinite'
                }} />
              </div>
            </div>
            
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '0.75rem',
              fontSize: '0.8125rem'
            }}>
              <span style={{ color: '#718096', fontWeight: '500' }}>Progress</span>
              <span style={{ color: '#c29e5d', fontWeight: '700', fontSize: '0.875rem' }}>
                {loadingProgress.current}%
              </span>
            </div>
          </div>

          {/* Loading Steps Indicator */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '0.5rem',
            marginBottom: '2rem'
          }}>
            {['permissions', 'mapping', 'employees', 'finalizing'].map((step, index) => {
              const stages = ['permissions', 'mapping', 'employees', 'finalizing'];
              const currentIndex = stages.indexOf(loadingProgress.stage);
              const isCompleted = index < currentIndex;
              const isCurrent = step === loadingProgress.stage;
              
              return (
                <div
                  key={step}
                  style={{
                    width: '0.75rem',
                    height: '0.75rem',
                    borderRadius: '50%',
                    background: isCompleted 
                      ? '#c29e5d' 
                      : isCurrent 
                        ? '#d4b373' 
                        : '#e2e8f0',
                    transition: 'all 0.3s ease',
                    transform: isCurrent ? 'scale(1.2)' : 'scale(1)'
                  }}
                />
              );
            })}
          </div>

          {/* Estimated Time */}
          <div style={{
            fontSize: '0.8125rem',
            color: '#718096',
            fontStyle: 'italic',
            lineHeight: '1.5'
          }}>
            Fast loading - No attendance check required
          </div>

          {/* Company Footer */}
          <div style={{
            marginTop: '2rem',
            paddingTop: '1.5rem',
            borderTop: '1px solid #e2e8f0',
            fontSize: '0.75rem',
            color: '#a0aec0',
            fontWeight: '500'
          }}>
            PHOENICIAN TECHNICAL SERVICES LLC
          </div>

          {/* CSS Animation */}
          <style>{`
            @keyframes shine {
              0% { left: -100%; }
              100% { left: 100%; }
            }
          `}</style>
        </div>
      </div>
    );
  }

  // Error state
  if (errors.general) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorContent}>
          <ExclamationTriangleIcon className={styles.errorIcon} />
          <h3 className={styles.errorTitle}>Error Loading Form</h3>
          <p className={styles.errorMessage}>{errors.general}</p>
          <button onClick={handleRefresh} className={styles.retryButton}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.createOvertimeContainer}>
      <div className={styles.pageWrapper}>
        {/* Page Header */}
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>No Break Management</h1>
          <p className={styles.pageSubtitle}>Mark employees who worked without breaks - validation when data syncs</p>
          
          {/* Refresh Button */}
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className={styles.refreshButton}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: '#f3f4f6',
              color: '#374151',
              border: '2px solid #e5e7eb',
              padding: '0.75rem 1rem',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}
          >
            <ArrowPathIcon style={{ width: '1rem', height: '1rem' }} />
            Refresh Data
          </button>
        </div>

        {/* 🆕 UPDATED: Deferred Validation Banner (Like Overtime) */}
        <div style={{
          background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
          border: '2px solid #3b82f6',
          borderRadius: '1rem',
          padding: '1.5rem',
          marginBottom: '2rem',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '1rem'
        }}>
          <InformationCircleIcon style={{
            width: '2rem',
            height: '2rem',
            color: '#1d4ed8',
            flexShrink: 0,
            marginTop: '0.125rem'
          }} />
          <div style={{ flex: 1 }}>
            <h3 style={{
              margin: '0 0 0.5rem 0',
              fontSize: '1.125rem',
              fontWeight: '600',
              color: '#1e3a8a'
            }}>
              📅 Attendance Validation Process
            </h3>
            <div style={{
              fontSize: '0.875rem',
              color: '#1e40af',
              lineHeight: '1.6'
            }}>
              <p style={{ margin: '0 0 0.5rem 0' }}>
                <strong>You can now select ANY eligible employee for no-break status.</strong> The system will automatically validate their attendance when offline data syncs (usually overnight).
              </p>
              <ul style={{ margin: '0.5rem 0 0 1.25rem', paddingLeft: 0 }}>
                <li>✅ <strong>Today:</strong> Create no-break request and select employees freely</li>
                <li>📱 <strong>Tonight:</strong> Offline employees' attendance data syncs when they reach home</li>
                <li>⏰ <strong>Tomorrow 6:00 AM:</strong> System validates attendance automatically</li>
                <li>📊 <strong>Dashboard:</strong> View validation results and status updates</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Submission Result */}
        {submissionResult && (
          <div className={`${styles.submissionResult} ${
            submissionResult.success ? styles.success : styles.error
          }`}>
            <div className={styles.submissionResultContent}>
              {submissionResult.success ? (
                <CheckCircleIcon className={`${styles.submissionResultIcon} ${styles.success}`} />
              ) : (
                <ExclamationTriangleIcon className={`${styles.submissionResultIcon} ${styles.error}`} />
              )}
              <div className={styles.submissionResultText}>
                <span className={`${styles.submissionResultMessage} ${
                  submissionResult.success ? styles.success : styles.error
                }`}>
                  {submissionResult.message}
                </span>
                {submissionResult.success && submissionResult.requestId && (
                  <p className={`${styles.submissionResultId} ${styles.success}`}>
                    Request ID: {submissionResult.requestId.slice(0, 8)}...
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <div className={styles.stepPanel}>
          {/* Project & Date Section */}
          <div className={styles.stepHeader}>
            <BuildingOfficeIcon className={styles.stepHeaderIcon} />
            <div>
              <h2 className={styles.stepHeaderTitle}>Project Details</h2>
              <p className={styles.stepHeaderSubtitle}>Enter project information for this no-break request</p>
            </div>
          </div>

          <div className={styles.stepBody}>
            <div className={styles.inputGrid}>
              <div className={styles.inputGroup}>
                <label htmlFor="projectName" className={styles.inputLabel}>
                  Project Name <span className={styles.required}>*</span>
                </label>
                <input
                  id="projectName"
                  type="text"
                  value={formData.projectName}
                  onChange={(e) => setFormData(prev => ({ ...prev, projectName: e.target.value }))}
                  className={`${styles.textInput} ${errors.projectName ? styles.error : ''}`}
                  placeholder="Enter project name"
                  disabled={isSubmitting}
                />
                {errors.projectName && (
                  <p className={styles.errorText}>{errors.projectName}</p>
                )}
              </div>

              <div className={styles.inputGroup}>
                <label htmlFor="projectCode" className={styles.inputLabel}>
                  Project Code <span className={styles.required}>*</span>
                </label>
                <input
                  id="projectCode"
                  type="text"
                  value={formData.projectCode}
                  onChange={(e) => setFormData(prev => ({ ...prev, projectCode: e.target.value }))}
                  className={`${styles.textInput} ${errors.projectCode ? styles.error : ''}`}
                  placeholder="Enter project code"
                  disabled={isSubmitting}
                />
                {errors.projectCode && (
                  <p className={styles.errorText}>{errors.projectCode}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.stepPanel}>
          {/* Date & Reason Section */}
          <div className={styles.stepHeader}>
            <CalendarDaysIcon className={styles.stepHeaderIcon} />
            <div>
              <h2 className={styles.stepHeaderTitle}>Date & Reason</h2>
              <p className={styles.stepHeaderSubtitle}>Set the date and reason for no-break marking</p>
            </div>
          </div>

          <div className={styles.stepBody}>
            <div className={styles.inputGrid}>
              <div className={styles.inputGroup}>
                <label htmlFor="selectedDate" className={styles.inputLabel}>
                  Date <span className={styles.required}>*</span>
                </label>
                <input
                  id="selectedDate"
                  type="date"
                  value={formData.selectedDate.toISOString().split('T')[0]}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    selectedDate: new Date(e.target.value) 
                  }))}
                  className={`${styles.textInput} ${errors.date ? styles.error : ''}`}
                  disabled={isSubmitting}
                  max={new Date().toISOString().split('T')[0]}
                />
                {errors.date && (
                  <p className={styles.errorText}>{errors.date}</p>
                )}
              </div>

              <div className={styles.inputGroup}>
                <label htmlFor="reason" className={styles.inputLabel}>
                  Reason for No Break <span className={styles.required}>*</span>
                </label>
                <textarea
                  id="reason"
                  value={formData.reason}
                  onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                  className={`${styles.textInput} ${errors.reason ? styles.error : ''}`}
                  placeholder="Enter reason why these employees worked without breaks"
                  disabled={isSubmitting}
                  rows={3}
                  style={{ resize: 'vertical', minHeight: '80px' }}
                />
                {errors.reason && (
                  <p className={styles.errorText}>{errors.reason}</p>
                )}
                <div style={{
                  textAlign: 'right',
                  fontSize: '0.75rem',
                  color: formData.reason.length > 200 ? '#d97706' : '#6b7280',
                  marginTop: '0.25rem'
                }}>
                  {formData.reason.length}/500 characters
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* PIN Search Section */}
        <div className={styles.stepPanel}>
          <div className={styles.stepHeader}>
            <UserIcon className={styles.stepHeaderIcon} />
            <div>
              <h2 className={styles.stepHeaderTitle}>Quick Employee Search</h2>
              <p className={styles.stepHeaderSubtitle}>Search by PIN/AUX number to add individual employees</p>
            </div>
          </div>

          <div className={styles.stepBody}>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
              <div className={styles.inputGroup} style={{ flex: 1 }}>
                <label htmlFor="searchPin" className={styles.inputLabel}>
                  Employee PIN/AUX Number
                </label>
                <input
                  id="searchPin"
                  type="text"
                  value={formData.searchPin}
                  onChange={(e) => setFormData(prev => ({ ...prev, searchPin: e.target.value }))}
                  className={`${styles.textInput} ${errors.pin ? styles.error : ''}`}
                  placeholder="Enter PIN/AUX number (e.g., 3576, 1289)"
                  disabled={isSubmitting || isSearchingPin}
                  onKeyPress={(e) => e.key === 'Enter' && handlePinSearch()}
                />
              </div>
              <button
                type="button"
                onClick={handlePinSearch}
                disabled={isSubmitting || isSearchingPin || !formData.searchPin.trim()}
                className={`${styles.navButton} ${styles.primary}`}
                style={{ minWidth: '120px' }}
              >
                {isSearchingPin ? (
                  <>
                    <div className={styles.submitSpinner}></div>
                    Searching...
                  </>
                ) : (
                  <>
                    <MagnifyingGlassIcon className={styles.navIcon} />
                    Search
                  </>
                )}
              </button>
            </div>

            {errors.pin && (
              <p className={styles.errorText}>{errors.pin}</p>
            )}

            {/* PIN Search Result */}
            {pinSearchResult && (
              <div style={{
                marginTop: '1rem',
                padding: '1rem',
                background: 'linear-gradient(135deg, #f0fdf4, #f7fee7)',
                border: '1px solid #bbf7d0',
                borderRadius: '0.75rem'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h4 style={{ margin: '0 0 0.5rem 0', color: '#047857' }}>
                      Employee Found
                    </h4>
                    <p style={{ margin: '0', fontSize: '0.875rem', color: '#059669' }}>
                      <strong>{pinSearchResult.name}</strong> - {pinSearchResult.designation}
                    </p>
                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#059669' }}>
                      PIN: {pinSearchResult.pin} | Department: {pinSearchResult.department}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleAddPinResult}
                    disabled={formData.selectedEmployeeIds.includes(pinSearchResult.id)}
                    className={`${styles.navButton} ${styles.submit} ${styles.enabled}`}
                    style={{ minWidth: '100px' }}
                  >
                    {formData.selectedEmployeeIds.includes(pinSearchResult.id) ? 'Added' : 'Add'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Employee Selection Section */}
        <div className={styles.stepPanel}>
          <div className={styles.stepHeader}>
            <UserGroupIcon className={styles.stepHeaderIcon} />
            <div>
              <h2 className={styles.stepHeaderTitle}>Select Employees</h2>
              <p className={styles.stepHeaderSubtitle}>
                Choose employees for no-break on {formData.selectedDate.toLocaleDateString()}
              </p>
            </div>
          </div>

          <div className={styles.stepBody}>
            {/* Excel Upload Section */}
            <div className={styles.uploadSection}>
              <div className={styles.uploadHeader}>
                <DocumentArrowUpIcon className={styles.uploadIcon} />
                <div>
                  <h3 className={styles.uploadTitle}>Bulk Upload via Excel</h3>
                  <p className={styles.uploadSubtitle}>Upload Excel file with AUX column</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowExcelUpload(!showExcelUpload)}
                  className={styles.toggleUploadButton}
                >
                  {showExcelUpload ? 'Hide Upload' : 'Show Upload'}
                </button>
              </div>

              {showExcelUpload && (
                <div className={styles.uploadPanel}>
                  <div className={styles.uploadInstructions}>
                    <div className={styles.instructionHeader}>
                      <h4>Upload Instructions:</h4>
                      <button
                        type="button"
                        onClick={() => excelProcessingService.downloadSampleTemplate()}
                        className={styles.downloadTemplateButton}
                      >
                        Download Template
                      </button>
                    </div>
                    <ul>
                      <li>Excel file should have a column named "AUX"</li>
                      <li>AUX column should contain employee numbers (e.g., 3576, 1289, etc.)</li>
                      <li>System will match these numbers with available employees</li>
                      <li>Validation will occur when attendance data syncs</li>
                    </ul>
                  </div>

                  <div className={styles.uploadArea}>
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleExcelUpload}
                      className={styles.fileInput}
                      id="excel-upload"
                      disabled={isUploadingExcel}
                    />
                    <label htmlFor="excel-upload" className={styles.fileLabel}>
                      {isUploadingExcel ? (
                        <>
                          <div className={styles.uploadSpinner}></div>
                          Processing Excel file...
                        </>
                      ) : (
                        <>
                          <DocumentArrowUpIcon className={styles.uploadLabelIcon} />
                          Click to upload Excel file
                        </>
                      )}
                    </label>
                  </div>

                  {/* Excel Upload Results */}
                  {excelUploadResult && (
                    <div className={`${styles.uploadResult} ${
                      excelUploadResult.success ? styles.success : styles.error
                    }`}>
                      <div className={styles.uploadResultHeader}>
                        {excelUploadResult.success ? (
                          <CheckCircleIcon className={styles.uploadResultIcon} />
                        ) : (
                          <ExclamationCircleIcon className={styles.uploadResultIcon} />
                        )}
                        <div>
                          <h4>Upload Results</h4>
                          <p>
                            {excelUploadResult.matched.length} of {excelUploadResult.total} employees matched
                          </p>
                        </div>
                      </div>

                      {excelUploadResult.matched.length > 0 && (
                        <div className={styles.matchedEmployees}>
                          <div className={styles.matchedHeader}>
                            <h5>Matched and Added ({excelUploadResult.matched.length}):</h5>
                          </div>
                          <div className={styles.matchedList}>
                            {excelUploadResult.matched.map((emp: any) => (
                              <span key={emp.id} className={styles.matchedTag}>
                                {emp.name} ({emp.pin || emp.employeeNumber})
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {excelUploadResult.failed.length > 0 && (
                        <div className={styles.failedEmployees}>
                          <div className={styles.failedHeader}>
                            <h5>Not Found ({excelUploadResult.failed.length}):</h5>
                          </div>
                          <div className={styles.failedList}>
                            {excelUploadResult.failed.slice(0, 10).map((aux: string) => (
                              <span key={aux} className={styles.failedTag}>{aux}</span>
                            ))}
                            {excelUploadResult.failed.length > 10 && (
                              <span className={styles.moreFailedTag}>
                                +{excelUploadResult.failed.length - 10} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Selected Employees Summary */}
            {formData.selectedEmployeeIds.length > 0 && (
              <div className={styles.selectedSummary}>
                <div className={styles.summaryHeader}>
                  <UserGroupIcon className={styles.summaryIcon} />
                  <h3>Selected Employees ({formData.selectedEmployeeIds.length})</h3>
                  <button
                    type="button"
                    onClick={handleClearAllEmployees}
                    className={styles.clearAllButton}
                    disabled={isSubmitting}
                  >
                    Clear All
                  </button>
                </div>
                <div className={styles.selectedTags}>
                  {selectedEmployees.slice(0, 10).map(employee => (
                    <span key={employee.id} className={styles.selectedTag}>
                      {employee.name}
                      <button
                        type="button"
                        onClick={() => handleEmployeeToggle(employee.id)}
                        className={styles.removeTagButton}
                        disabled={isSubmitting}
                      >
                        <XMarkIcon />
                      </button>
                    </span>
                  ))}
                  {selectedEmployees.length > 10 && (
                    <span className={styles.moreTag}>
                      +{selectedEmployees.length - 10} more
                    </span>
                  )}
                </div>
              </div>
            )}

            {errors.employees && (
              <p className={styles.errorText}>{errors.employees}</p>
            )}

            {/* 🆕 UPDATED: Statistics - No attendance checking */}
            {eligibleEmployees.length > 0 && (
              <div style={{
                background: 'linear-gradient(135deg, #f0f9ff, #e0f2fe)',
                border: '1px solid #0ea5e9',
                borderRadius: '1rem',
                padding: '1.5rem',
                marginBottom: '2rem'
              }}>
                <h3 style={{
                  margin: '0 0 1rem 0',
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  color: '#0c4a6e'
                }}>
                  Available Employees for {formData.selectedDate.toLocaleDateString()}
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#0284c7' }}>
                      {eligibleEmployees.length}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#0ea5e9', fontWeight: '500' }}>
                      Total Eligible
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#7c3aed' }}>
                      {formData.selectedEmployeeIds.length}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#8b5cf6', fontWeight: '500' }}>
                      Selected for No-Break
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Manual Employee Selection */}
            <div className={styles.manualSelection}>
              <div className={styles.selectionHeader}>
                <h3>Manual Selection</h3>
                <p>Search and select employees individually - validation when data syncs</p>
              </div>

              {/* Search Bar */}
              <div className={styles.searchContainer}>
                <MagnifyingGlassIcon className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Search by name, AUX number, designation, department..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={styles.searchInput}
                  disabled={isSubmitting}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className={styles.clearSearch}
                  >
                    <XMarkIcon className={styles.clearSearchIcon} />
                  </button>
                )}
              </div>

              {/* Action Buttons */}
              <div className={styles.actionButtons}>
                <button
                  type="button"
                  onClick={handleSelectAllEmployees}
                  className={`${styles.actionButton} ${styles.green}`}
                  disabled={isSubmitting || filteredEmployees.length === 0}
                >
                  Select All ({filteredEmployees.length})
                </button>
                <div className={styles.employeeStats}>
                  Showing {filteredEmployees.length} employees
                </div>
              </div>

              {/* Employee List */}
              <div className={styles.employeeList}>
                {filteredEmployees.length > 0 ? (
                  <div className={styles.employeeGrid}>
                    {filteredEmployees.map(employee => {
                      const isSelected = formData.selectedEmployeeIds.includes(employee.id);
                      
                      return (
                        <label
                          key={employee.id}
                          className={`${styles.employeeCard} ${
                            isSelected ? styles.selected : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleEmployeeToggle(employee.id)}
                            className={styles.employeeCheckbox}
                            disabled={isSubmitting}
                          />
                          <div className={styles.employeeInfo}>
                            <div className={styles.employeeName}>
                              {employee.name}
                            </div>
                            <div className={styles.employeeDetails}>
                              AUX: {employee.pin || employee.employeeNumber || 'N/A'}
                            </div>
                            <div className={styles.employeeDetails}>
                              {employee.designation}
                            </div>
                            {employee.department && employee.department !== 'No department' && (
                              <div className={styles.employeeDepartment}>
                                {employee.department}
                              </div>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <div className={styles.emptyState}>
                    {searchQuery ? 'No employees found matching your search.' : 'No employees available.'}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Button */}
        <div className={styles.navigationButtons}>
          <div className={styles.navSpacer} />
          
          <button
            type="button"
            onClick={handleSubmit}
            disabled={
              isSubmitting ||
              !formData.projectName.trim() ||
              !formData.projectCode.trim() ||
              !formData.reason.trim() || 
              formData.selectedEmployeeIds.length === 0
            }
            className={`${styles.navButton} ${styles.submit} ${
              isSubmitting ||
              !formData.projectName.trim() ||
              !formData.projectCode.trim() ||
              !formData.reason.trim() || 
              formData.selectedEmployeeIds.length === 0
                ? styles.disabled
                : styles.enabled
            }`}
          >
            {isSubmitting ? (
              <>
                <div className={styles.submitSpinner}></div>
                Submitting...
              </>
            ) : (
              <>
                <CheckCircleIcon className={styles.navIcon} />
                Submit Request ({formData.selectedEmployeeIds.length} employees)
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NoBreakPage;